import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { SpTooltip } from 'components/shared/Tooltip/Tooltip';
import type { IntlKey } from 'locales';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

const useFallbackLabel = (label?: string, labelKey?: IntlKey) => {
  const { formatMessage } = useIntl();
  return isDefined(label) ? label : isDefined(labelKey) ? formatMessage({ id: labelKey }) : undefined;
};
const StyledTooltipDiv = styled.div`
  max-width: 500px !important;
  min-width: 200px !important;
`;

export type IWithLabelProps = {
  className?: string;
  labelClassName?: string;
  children?: ReactNode;
  colSize?: number;
  id: string;
  useFlex?: boolean;
  isAlignedLeft?: boolean;
  wrapText?: boolean;
  icon?: string;
  tooltip?: string;
  label?: string;
  labelKey?: IntlKey;
};

export const WithLabel: FC<IWithLabelProps> = ({
  id,
  className,
  labelClassName,
  isAlignedLeft,
  wrapText,
  colSize = 12,
  tooltip,
  icon,
  children,
  label,
  labelKey,
  useFlex = false,
}: IWithLabelProps) => {
  const formattedLabel = useFallbackLabel(label, labelKey);
  const childrenCol = 12 - colSize;

  return (
    <div className={classNames('form-group', className, { row: !useFlex, 'd-flex': useFlex })} data-e2e={id}>
      <SpTooltip
        style={{ padding: 0 }}
        disabled={Boolean(icon || !tooltip)}
        contents={
          <StyledTooltipDiv
            className="text-secondary text-start p-2 bg-primary-alt shadow"
            dangerouslySetInnerHTML={tooltip ? { __html: tooltip } : undefined}
          />
        }
      >
        <label
          className={classNames(
            `col-sm-${colSize} text-truncate text-secondary`,
            {
              'col-form-label': isAlignedLeft !== true,
              'text-wrap': wrapText === true,
              'text-nowrap': wrapText !== true,
            },
            labelClassName,
          )}
          htmlFor={id}
          title={tooltip ? '' : formattedLabel}
        >
          {formattedLabel}
          {icon && (
            <SpTooltip
              style={{ padding: 0 }}
              disabled={Boolean(!tooltip)}
              contents={
                <StyledTooltipDiv
                  className="text-secondary text-start p-2 bg-primary-alt shadow"
                  dangerouslySetInnerHTML={tooltip ? { __html: tooltip } : undefined}
                />
              }
            >
              <i className="icon icon-sm text-secondary ms-2" id={`${id}-icon`}>
                {icon}
              </i>
            </SpTooltip>
          )}
        </label>
      </SpTooltip>

      <div
        className={classNames(`my-auto`, childrenCol === 0 ? 'col-sm-12' : `col-sm-${childrenCol}`, {
          'd-flex flex-row-reverse': isAlignedLeft === true,
        })}
      >
        {children}
      </div>
    </div>
  );
};
export type WithFormLabelProps = Pick<
  IWithLabelProps,
  'colSize' | 'useFlex' | 'label' | 'labelKey' | 'children' | 'id' | 'className'
>;
export const WithFormLabel: FC<IWithLabelProps> = ({
  colSize = 12,
  useFlex = false,
  label,
  labelKey,
  children,
  id,
  className,
}: WithFormLabelProps) => {
  const formattedLabel = useFallbackLabel(label, labelKey);
  const childrenCol = 12 - colSize;
  return (
    <div className={classNames('form-group', className, { row: !useFlex, 'd-flex': useFlex })}>
      <label
        className={`col-sm-${colSize} col-form-label text-nowrap text-truncate`}
        htmlFor={id}
        title={formattedLabel}
      >
        {formattedLabel}
      </label>
      <div className={childrenCol === 0 ? 'col-sm-12' : `col-sm-${childrenCol}`}>{children}</div>
    </div>
  );
};
